/**
 * Contratos de entrada (Zod) de administración: autenticación, usuarios,
 * agencias, configuración de la organización y consulta de auditoría.
 */
import { z } from 'zod';
import { keyedEnum } from '../catalogs/enum.js';
import { PERMISSIONS, ROLES } from '../access/permissions.js';
import { normalizeRecoveryCode, RECOVERY_CODE_PATTERN, TOTP_CODE_PATTERN } from '../access/two-factor.js';
import { AUDIT_CATEGORIES } from '../catalogs/events.js';
import { isValidTimeZone } from '../calendar/guatemala.js';
import { RISK_CATEGORIES } from '../credit/delinquency.js';
import { ORGANIZATION_TYPES } from '../organization/profile.js';
import { addressSchema } from './client.js';
import { FelSettingsSchema } from './billing.js';
import { AccountingSettingsSchema } from './accounting.js';
import { DocumentSettingsSchema } from './documents.js';
import { basisPointsSchema, idSchema, isoDateSchema, nitSchema, optionalText, phoneSchema, TEXT_MAX } from './common.js';
// ── Autenticación y usuarios ────────────────────────────────────────────────
/** Longitud mínima de contraseña: para el personal de campo, larga es mejor que compleja. */
export const PASSWORD_MIN_LENGTH = 10;
/** Longitud máxima de contraseña (bcrypt solo usa los primeros 72 bytes; 128 deja margen). */
export const PASSWORD_MAX_LENGTH = 128;
/** Al menos una letra, incluidas tildes y eñe. */
const PASSWORD_LETTER_PATTERN = /[A-Za-zÁÉÍÓÚáéíóúÑñ]/;
/** Al menos un dígito. */
const PASSWORD_DIGIT_PATTERN = /\d/;
/** Contraseña de 10+ caracteres con letras y números. */
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `La contraseña debe tener al menos ${String(PASSWORD_MIN_LENGTH)} caracteres`)
    .max(PASSWORD_MAX_LENGTH)
    .regex(PASSWORD_LETTER_PATTERN, 'Incluye al menos una letra')
    .regex(PASSWORD_DIGIT_PATTERN, 'Incluye al menos un número');
/** Inicio de sesión (el token de Turnstile es opcional según el entorno). */
export const LoginSchema = z.object({
    email: z.string().trim().toLowerCase().email('Correo inválido'),
    password: z.string().min(1, 'Ingresa tu contraseña'),
    turnstileToken: z.string().optional(),
});
// ── Verificación en dos pasos ───────────────────────────────────────────────
/** Longitud máxima del token de desafío (base64url de 32 bytes con holgura). */
const CHALLENGE_TOKEN_MAX_LENGTH = 128;
/** Longitud máxima de un código de recuperación escrito con guiones o espacios. */
const RECOVERY_CODE_INPUT_MAX_LENGTH = 24;
/** Código TOTP de seis dígitos (se toleran espacios: «123 456»). */
export const totpCodeSchema = z
    .string()
    .transform((value) => value.replace(/\s/g, ''))
    .pipe(z.string().regex(TOTP_CODE_PATTERN, 'El código tiene 6 dígitos'));
/** Código de recuperación («ABCDE-FGHJK»); se normaliza a mayúsculas sin guiones. */
export const recoveryCodeSchema = z
    .string()
    .max(RECOVERY_CODE_INPUT_MAX_LENGTH)
    .transform(normalizeRecoveryCode)
    .pipe(z.string().regex(RECOVERY_CODE_PATTERN, 'Código de recuperación inválido'));
/**
 * Segundo paso del inicio de sesión: el token de desafío que devolvió
 * `/auth/login` y un código TOTP **o** un código de recuperación.
 */
export const TwoFactorLoginSchema = z
    .object({
    challengeToken: z.string().min(1).max(CHALLENGE_TOKEN_MAX_LENGTH),
    code: totpCodeSchema.optional(),
    recoveryCode: recoveryCodeSchema.optional(),
})
    .refine((d) => (d.code === undefined) !== (d.recoveryCode === undefined), {
    message: 'Ingresa el código de tu aplicación o un código de recuperación',
    path: ['code'],
});
/** Confirmación de la activación con el primer código de la aplicación. */
export const ConfirmTwoFactorSchema = z.object({ code: totpCodeSchema });
/**
 * Reautenticación con la contraseña propia antes de una acción sensible
 * (desactivar el segundo factor, regenerar códigos, restablecer el segundo
 * factor de otro usuario).
 */
export const PasswordConfirmationSchema = z.object({ password: z.string().min(1, 'Ingresa tu contraseña').max(PASSWORD_MAX_LENGTH) });
/** Renovación de sesión con el refresh token. */
export const RefreshSessionSchema = z.object({ refreshToken: z.string().min(1) });
/** Cambio de contraseña del propio usuario. */
export const ChangePasswordSchema = z
    .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1),
})
    .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
})
    .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'La nueva contraseña debe ser distinta a la actual',
    path: ['newPassword'],
});
/** Campos editables de un usuario (empleado). */
const employeeFields = z.object({
    fullName: z.string().trim().min(3).max(TEXT_MAX.name),
    email: z.string().trim().toLowerCase().email(),
    role: z.enum(ROLES),
    branchId: idSchema.nullable(),
    phone: phoneSchema.optional(),
});
/** Alta de usuario con contraseña inicial. */
export const CreateEmployeeSchema = employeeFields.extend({ password: passwordSchema });
/** Edición de usuario por un administrador. */
export const UpdateEmployeeSchema = employeeFields.partial().extend({ isActive: z.boolean().optional() });
/** Temas de la interfaz. */
export const THEME_PREFERENCES = ['light', 'dark', 'system'];
/** Catálogo con clave de temas: `THEME_PREFERENCE.system`. */
export const THEME_PREFERENCE = keyedEnum(THEME_PREFERENCES);
/** Densidades de la interfaz. */
export const DENSITY_PREFERENCES = ['comfortable', 'compact'];
/** Límites de los widgets del tablero que el usuario puede fijar. */
const DASHBOARD_WIDGETS_MAX = 20;
const DASHBOARD_WIDGET_ID_MAX_LENGTH = 40;
/** Edición del propio perfil (sin rol de administrador). */
export const UpdateProfileSchema = z.object({
    fullName: z.string().trim().min(3).max(TEXT_MAX.name).optional(),
    phone: phoneSchema.optional(),
    preferences: z
        .object({
        theme: z.enum(THEME_PREFERENCES).optional(),
        density: z.enum(DENSITY_PREFERENCES).optional(),
        dashboardWidgets: z.array(z.string().max(DASHBOARD_WIDGET_ID_MAX_LENGTH)).max(DASHBOARD_WIDGETS_MAX).optional(),
    })
        .optional(),
});
/** Restablecimiento de contraseña por un administrador. */
export const ResetPasswordSchema = z.object({ newPassword: passwordSchema });
// ── Agencias ────────────────────────────────────────────────────────────────
/** Código de agencia: 2 a 6 letras mayúsculas o dígitos. */
const BRANCH_CODE_PATTERN = /^[A-Z0-9]{2,6}$/;
/** Máximo de feriados locales por agencia. */
const MAX_LOCAL_HOLIDAYS = 20;
/** Alta de una agencia. */
export const BranchSchema = z
    .object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(BRANCH_CODE_PATTERN, 'Código de 2 a 6 letras o números'),
    name: z.string().trim().min(3).max(TEXT_MAX.personName),
    phone: phoneSchema.optional(),
    isActive: z.boolean().default(true),
    /** Feria patronal y otros feriados locales del municipio. */
    localHolidays: z.array(isoDateSchema).max(MAX_LOCAL_HOLIDAYS).default([]),
})
    .merge(addressSchema);
/** Edición parcial de una agencia. */
export const UpdateBranchSchema = BranchSchema.partial();
// ── Configuración de la organización ────────────────────────────────────────
/** Límites de la configuración de la organización. */
const SETTINGS_LIMITS = {
    tradeNameMax: 80,
    addressMax: 300,
    phoneMax: 40,
    /** Tipo de cambio aceptable en centavos por US$ (Q1.00 – Q100.00). */
    exchangeRateMin: 100,
    exchangeRateMax: 10_000,
    riskLabelMax: 80,
    extraHolidaysMax: 50,
    reminderDayMax: 30,
    remindersMax: 5,
    /** El tono es un ángulo del círculo cromático. */
    accentHueMax: 360,
    shortNameMax: 24,
};
/** Política de seguridad: roles que deben usar verificación en dos pasos (sin repetidos). */
export const SecuritySettingsSchema = z.object({
    twoFactorRequiredRoles: z
        .array(z.enum(ROLES))
        .max(ROLES.length)
        .refine((roles) => new Set(roles).size === roles.length, 'Hay roles repetidos'),
});
/** Configuración completa de la organización. */
export const OrganizationSettingsSchema = z.object({
    legalName: z.string().trim().min(3).max(TEXT_MAX.name),
    tradeName: z.string().trim().min(2).max(SETTINGS_LIMITS.tradeNameMax),
    nit: nitSchema,
    address: z.string().trim().min(3).max(SETTINGS_LIMITS.addressMax),
    phone: z.string().trim().max(SETTINGS_LIMITS.phoneMax),
    email: z.string().trim().max(TEXT_MAX.personName),
    organizationType: z.enum(ORGANIZATION_TYPES),
    timeZone: z.string().refine(isValidTimeZone, 'Zona horaria IANA inválida (ej. America/Guatemala)'),
    taxOnInterest: basisPointsSchema,
    exchangeRateCentsPerUsd: z.number().int().min(SETTINGS_LIMITS.exchangeRateMin).max(SETTINGS_LIMITS.exchangeRateMax),
    riskCategoryRules: z
        .array(z.object({
        category: z.enum(RISK_CATEGORIES),
        label: z.string().min(2).max(SETTINGS_LIMITS.riskLabelMax),
        maxDaysPastDue: z.number().min(0),
        provisionRate: basisPointsSchema,
    }))
        .length(RISK_CATEGORIES.length),
    capacityPolicy: z.object({
        maxInstallmentToIncome: basisPointsSchema,
        lowRiskThreshold: basisPointsSchema,
    }),
    rolePermissions: z.record(z.enum(ROLES), z.array(z.enum(PERMISSIONS))),
    extraHolidays: z.array(isoDateSchema).max(SETTINGS_LIMITS.extraHolidaysMax),
    reminderDaysBefore: z.array(z.number().int().min(0).max(SETTINGS_LIMITS.reminderDayMax)).max(SETTINGS_LIMITS.remindersMax),
    brand: z.object({
        accentHue: z.number().int().min(0).max(SETTINGS_LIMITS.accentHueMax),
        logoUrl: z.string().url().max(TEXT_MAX.shortText).nullable(),
        shortName: z.string().trim().min(2).max(SETTINGS_LIMITS.shortNameMax),
    }),
    features: z.object({
        aiAssistant: z.boolean(),
        documentOcr: z.boolean(),
        pushNotifications: z.boolean(),
        whatsappReminders: z.boolean(),
        collateralLoans: z.boolean(),
        complianceScreening: z.boolean(),
    }),
    documents: DocumentSettingsSchema,
    fel: FelSettingsSchema,
    security: SecuritySettingsSchema,
    accounting: AccountingSettingsSchema,
});
/** Edición parcial de la configuración. */
export const UpdateOrganizationSettingsSchema = OrganizationSettingsSchema.partial();
/** Longitud máxima de un tipo de entidad o acción en los filtros de auditoría. */
const AUDIT_FILTER_MAX_LENGTH = 40;
const AUDIT_ACTION_FILTER_MAX_LENGTH = 60;
/** Filtros de la bitácora de auditoría. */
export const auditQuerySchema = z.object({
    entityType: z.string().max(AUDIT_FILTER_MAX_LENGTH).optional(),
    entityId: idSchema.optional(),
    employeeId: idSchema.optional(),
    action: optionalText(AUDIT_ACTION_FILTER_MAX_LENGTH),
    /** Grupo de acciones, p. ej. `security` (accesos, segundo factor, sesiones). */
    category: z.enum(AUDIT_CATEGORIES).optional(),
});
//# sourceMappingURL=administration.js.map